<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useMutation } from '@tanstack/vue-query';
import { attendancesApi, type Attendance, type AttendanceUpdateForm } from '@/api/attendances';
import TimeInput from '@/components/time-input.vue';
import { type AxiosError } from 'axios';

const { t } = useI18n();

const props = defineProps<{
  attendance: Attendance;
  attendanceType: 'checkIn' | 'checkOut';
  canEdit: boolean;
}>();

const emit = defineEmits(['success', 'refetch']);

const MAX_REASON_LENGTH = 250;

const validationSchema = yup.object({
  time: yup.string().required(t('attendanceEditForm.timeRequired')),
  reason: yup.string()
    .required(t('attendanceEditForm.reasonRequired'))
    .max(MAX_REASON_LENGTH, t('attendanceEditForm.reasonMaxLength')),
});

const initialValues = {
  time: props.attendance.humanTime,
  reason: '',
};

const { handleSubmit, values, errors, defineField, resetForm } = useForm({
  validationSchema,
  initialValues,
});

const [time] = defineField('time');
const [reason, reasonAttrs] = defineField('reason');

const hasBeenUpdated = computed(() => props.attendance.markedAtHasBeenUpdated);

const currentAttendanceMessage = computed(() => {
  if (hasBeenUpdated.value) {
    return `Marca modificada al día ${props.attendance.humanDate} a las ${props.attendance.humanTime}. ` +
      'Puedes volver a la hora original si el trabajador rechaza la modificación.';
  }

  return `Marca actual el día ${props.attendance.humanDate} a las ${props.attendance.humanTime}.`;
});

const { mutate, isPending } = useMutation<boolean, AxiosError, AttendanceUpdateForm>({
  mutationFn: (attendanceUpdateForm: AttendanceUpdateForm) =>
    attendancesApi.update(props.attendance.id, attendanceUpdateForm),
  onSuccess: () => {
    emit('success');
    emit('refetch');
    resetForm();
  },
});

function updatedMarkedAt(markedAt: Date, newTime: string): string {
  const [hours, minutes] = newTime.split(':').map(Number) as [number, number];
  const newDateTime = new Date(markedAt);
  newDateTime.setHours(hours, minutes, 0, 0);

  return newDateTime.toISOString();
}

const onSubmit = handleSubmit(() => {
  const markedAt = updatedMarkedAt(props.attendance.markedAt, values.time);
  const markedAtEditReason = values.reason.trim();

  mutate({ markedAt, markedAtEditReason });
});

function revertToOriginal() {
  mutate({ markedAt: '', markedAtEditReason: '' });
}

const isCheckIn = computed(() => props.attendanceType === 'checkIn');
const icon = computed(() => (isCheckIn.value ? 'mdi-login' : 'mdi-logout'));
const title = computed(() => (isCheckIn.value ? 'Entrada' : 'Salida'));
const label = computed(() => (isCheckIn.value ? 'Nueva hora de entrada' : 'Nueva hora de salida'));
const buttonText = computed(() => {
  if (hasBeenUpdated.value) {
    return 'Volver a Original';
  }

  return isCheckIn.value ? 'Actualizar entrada' : 'Actualizar salida';
});
const buttonVariant = computed(() => {
  if (hasBeenUpdated.value) {
    return 'outlined';
  }

  return 'flat';
});
</script>

<template>
  <v-col
    v-if="canEdit"
    cols="12"
    md="6"
  >
    <v-card
      variant="outlined"
      class="pa-4"
    >
      <v-card-title class="align-center d-flex text-h6 text-primary">
        <v-icon
          :icon="icon"
          class="mr-2"
        />
        {{ title }}
      </v-card-title>
      <v-card-text>
        <p class="mt-2 mb-6 text-subtitle-2">
          {{ currentAttendanceMessage }}
        </p>
        <v-form
          v-if="!hasBeenUpdated"
          @submit.prevent="onSubmit"
        >
          <div class="d-flex flex-column ga-4">
            <time-input
              v-model="time"
              :label="label"
              :disabled="isPending"
              :error-messages="errors.time"
            />
            <v-textarea
              v-model="reason"
              label="Motivo de la modificación"
              placeholder="Ingresa el motivo por el cual se modifica la hora de marcación..."
              variant="outlined"
              :disabled="isPending"
              density="compact"
              rows="3"
              :counter="MAX_REASON_LENGTH"
              :error-messages="errors.reason"
              v-bind="reasonAttrs"
            />
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="hasBeenUpdated"
          color="primary"
          :variant="buttonVariant"
          :loading="isPending"
          @click="revertToOriginal"
        >
          {{ buttonText }}
        </v-btn>
        <v-btn
          v-else
          color="primary"
          :variant="buttonVariant"
          :loading="isPending"
          @click="onSubmit"
        >
          {{ buttonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-col>
</template>
